import { el, clear, svgIcon, ICONS } from "./components";
import type { Question, MCQuestion, ConjQuestion, SentenceQuestion } from "../quiz";
import { checkAnswer } from "../quiz";
import type { AudioPlayer } from "../audio";

export type Quality = 1 | 3 | 4 | 5;

export interface CardCallbacks {
  onResult: (correct: boolean) => void;
  onAdvance: (quality: Quality) => void;
  audio: AudioPlayer;
  audioEnabled: boolean;
  autoAdvanceMs: number;
}

function categoryTag(cat: string): HTMLElement {
  return el("span", { class: "tag", text: cat });
}

function dirLabel(dir: "hr-de" | "de-hr"): string {
  return dir === "hr-de" ? "HR → DE" : "DE → HR";
}

// No buttons: correct → quality 4, wrong → quality 1. Auto-advance after the
// configured delay; a tap anywhere skips the wait. The listener is capture-phase
// so the answering click (already past document capture by the time the option
// handler runs) doesn't immediately trigger it.
function scheduleAdvance(correct: boolean, cb: CardCallbacks): void {
  const quality: Quality = correct ? 4 : 1;
  let fired = false;
  const go = (): void => {
    if (fired) return;
    fired = true;
    clearTimeout(timer);
    document.removeEventListener("click", go, true);
    cb.onAdvance(quality);
  };
  const timer = window.setTimeout(go, cb.autoAdvanceMs);
  document.addEventListener("click", go, true);
}

function renderMC(container: HTMLElement, q: MCQuestion, cb: CardCallbacks): void {
  const head = el("div", { class: "card-head" }, [
    categoryTag(q.card.cat),
    el("span", { class: "dir", text: dirLabel(q.dir) }),
  ]);

  const promptLabel = el("div", {
    class: "prompt-label",
    text: q.dir === "hr-de" ? "Übersetze ins Deutsche" : "Übersetze ins Kroatische",
  });

  const promptRow = el("div", { class: "prompt" });
  promptRow.append(q.prompt);
  if (q.dir === "hr-de" && cb.audioEnabled && cb.audio.isSupported() && q.speakText) {
    const speak = el(
      "button",
      {
        class: "speak-btn",
        attrs: { "aria-label": "Aussprache abspielen" },
        on: { click: () => cb.audio.speak(q.speakText!) },
        style: { marginLeft: "16px", verticalAlign: "middle" },
      },
      [svgIcon(ICONS.speaker)]
    );
    promptRow.appendChild(speak);
  }

  const opts = el("div", { class: "options" });
  const buttons: HTMLButtonElement[] = [];
  let answered = false;

  for (const option of q.options) {
    const btn = el("button", {
      class: "opt",
      text: option,
      on: {
        click: () => {
          if (answered) return;
          answered = true;
          const correct = checkAnswer(q, option);
          for (const b of buttons) {
            b.disabled = true;
            if (b.textContent === q.correctAnswer) b.classList.add("correct");
            else if (b === btn) b.classList.add("wrong");
          }
          if (cb.audioEnabled && q.speakText && cb.audio.isSupported()) {
            cb.audio.speak(q.speakText);
          }
          cb.onResult(correct);
          showFeedback(card, correct, q.correctAnswer, q.prompt);
          scheduleAdvance(correct, cb);
        },
      },
    }) as HTMLButtonElement;
    buttons.push(btn);
    opts.appendChild(btn);
  }

  const card = el("div", { class: "card" }, [head, promptLabel, promptRow, opts]);
  clear(container);
  container.appendChild(card);
}

function renderConj(container: HTMLElement, q: ConjQuestion, cb: CardCallbacks): void {
  const head = el("div", { class: "card-head" }, [
    categoryTag("Konjugacija"),
    el("span", { class: "dir", text: "Konj." }),
  ]);
  const promptLabel = el("div", { class: "prompt-label", text: "Konjugiere" });
  const promptRow = el("div", { class: "prompt" });
  promptRow.append(q.prompt);

  const opts = el("div", { class: "options" });
  const buttons: HTMLButtonElement[] = [];
  let answered = false;

  for (const option of q.options) {
    const btn = el("button", {
      class: "opt",
      text: option,
      on: {
        click: () => {
          if (answered) return;
          answered = true;
          const correct = checkAnswer(q, option);
          for (const b of buttons) {
            b.disabled = true;
            if (b.textContent === q.correctAnswer) b.classList.add("correct");
            else if (b === btn) b.classList.add("wrong");
          }
          if (cb.audioEnabled && cb.audio.isSupported()) {
            cb.audio.speak(q.speakText);
          }
          cb.onResult(correct);
          showFeedback(card, correct, q.correctAnswer, q.prompt);
          scheduleAdvance(correct, cb);
        },
      },
    }) as HTMLButtonElement;
    buttons.push(btn);
    opts.appendChild(btn);
  }

  const card = el("div", { class: "card" }, [head, promptLabel, promptRow, opts]);
  clear(container);
  container.appendChild(card);
}

function renderSentence(container: HTMLElement, q: SentenceQuestion, cb: CardCallbacks): void {
  const head = el("div", { class: "card-head" }, [
    categoryTag("Satz bauen"),
    el("span", { class: "dir", text: "DE → HR" }),
  ]);
  const promptLabel = el("div", { class: "prompt-label", text: "Bilde diesen Satz auf Kroatisch" });
  const promptRow = el("div", { class: "prompt" });
  promptRow.append(q.prompt);

  const targetRow = el("div", { class: "builder-target", attrs: { "aria-label": "Dein Satz" } });
  const trayRow = el("div", { class: "builder-tray", attrs: { "aria-label": "Wortkacheln" } });
  const feedback = el("div", { class: "builder-result", style: { marginTop: "12px" } });

  const placed: HTMLButtonElement[] = [];
  let answered = false;

  function check(): void {
    if (answered) return;
    answered = true;
    const order = placed.map(b => b.textContent || "");
    const correct = checkAnswer(q, order);
    feedback.classList.add(correct ? "ok" : "bad");
    feedback.textContent = correct ? `Richtig: ${q.card.hr}` : `Richtig wäre: ${q.card.hr}`;
    if (cb.audioEnabled && cb.audio.isSupported()) cb.audio.speak(q.speakText);
    cb.onResult(correct);
    scheduleAdvance(correct, cb);
  }

  function tokenButton(label: string): HTMLButtonElement {
    const t = el("button", {
      class: "token",
      text: label,
      on: {
        click: () => {
          if (answered) return;
          if (t.parentElement === trayRow) {
            trayRow.removeChild(t);
            placed.push(t);
            targetRow.appendChild(t);
          } else if (t.parentElement === targetRow) {
            targetRow.removeChild(t);
            const idx = placed.indexOf(t);
            if (idx !== -1) placed.splice(idx, 1);
            trayRow.appendChild(t);
          }
          // All tokens placed → auto-check, no button needed.
          if (!trayRow.querySelector(".token")) check();
        },
      },
    }) as HTMLButtonElement;
    return t;
  }

  for (const tk of q.tokens) trayRow.appendChild(tokenButton(tk));

  const card = el("div", { class: "card" }, [
    head,
    promptLabel,
    promptRow,
    el("div", { class: "prompt-label", text: "Dein Satz" }),
    targetRow,
    el("div", { class: "prompt-label", text: "Kacheln" }),
    trayRow,
    feedback,
  ]);

  clear(container);
  container.appendChild(card);
}

function showFeedback(card: HTMLElement, correct: boolean, expected: string, prompt: string): void {
  const fb = el("div", {
    class: `feedback ${correct ? "ok" : "bad"}`,
    text: correct
      ? `Richtig. ${prompt} → ${expected}`
      : `Nicht ganz. ${prompt} → ${expected}`,
  });
  card.appendChild(fb);
}

export function renderQuestion(container: HTMLElement, q: Question, cb: CardCallbacks): void {
  if (q.type === "mc") return renderMC(container, q, cb);
  if (q.type === "conj") return renderConj(container, q, cb);
  return renderSentence(container, q, cb);
}

export function renderEmpty(container: HTMLElement, onMoreNew: () => void): void {
  clear(container);
  const node = el("div", { class: "card" }, [
    el("div", { class: "empty" }, [
      el("div", { class: "big", text: "Sve gotovo za danas!" }),
      el("div", {
        text: "Du hast alle fälligen Karten heute gelernt. Komm morgen wieder oder hol dir extra neue Karten.",
      }),
      el("div", { style: { marginTop: "20px" } }, [
        el("button", {
          class: "btn btn-primary",
          text: "Mehr neue Karten heute",
          on: { click: onMoreNew },
        }),
      ]),
    ]),
  ]);
  container.appendChild(node);
}
